// Catalog.ID Client — Recovery Kit record bookkeeping
// ============================================================================
// The kit RECORD (`recoverykit`) is the node's non-secret view of the set —
// currency for the anonymous /recovery page, the Settings status line, the fire
// drill dates and the newest bundle fingerprint (registration-restructure
// §16.7). The kit ENTRY in the key bag is the member's secret view. Everything
// here is the record half, kept DOM-free so the decisions are unit-testable.
//
// Two rules the surfaces must not re-derive by hand:
//
//  - **Every bundle export refreshes the record** (M25). `POST …/recoverykit/
//    bundle` and its client call existed since the §5.3 bundle refresh and
//    NOTHING called them, so a bundle exported from Settings after the kit was
//    created made the fire drill announce "NOT the latest bundle on record".
//    The refresh is best-effort: the file is already on the member's disk when
//    it runs, so a failure is reported, never thrown.
//  - **A reprint reconciles, it does not re-`PUT`** — `PUT /api/account/
//    recoverykit` upserts and RESETS `lasttestedat`, so a reprint of the set
//    the record already names must go through the bundle endpoint instead
//    (§16.9). Only a missing record, or one naming another set, earns a `PUT`.

import type { RecoveryKitInfo } from '../api/account';

// ── M25: record the bundle every export produced ────────────────────────────

export interface RecordExportedBundleDeps {
    /** The kit on record, or null when the account has none (the API maps the
     *  404 to null). */
    getKit(): Promise<RecoveryKitInfo | null>;
    /** `POST /api/account/recoverykit/bundle`. */
    updateBundle(bundleFingerprint: string): Promise<void>;
    /** Surface a non-fatal problem to the member (a toast). Must not throw. */
    report(message: string): void;
}

/**
 * Point the kit record at a bundle that has just been exported. Never rejects:
 * the export succeeded before this ran and must not be made to look like a
 * failure. An account with no kit on record is silent — there is nothing to
 * refresh and nothing went wrong.
 */
export async function recordExportedBundle(
    params: { fingerprint: string },
    deps: RecordExportedBundleDeps,
): Promise<void> {
    let kit: RecoveryKitInfo | null;
    try {
        kit = await deps.getKit();
    } catch (err) {
        deps.report(`The backup was exported, but the Recovery Kit record could not be read `
            + `(${message(err)}). The fire drill may report this file as not the latest.`);
        return;
    }
    if (!kit) return;

    try {
        await deps.updateBundle(params.fingerprint);
    } catch (err) {
        deps.report(`The backup was exported, but the Recovery Kit record could not be updated `
            + `(${message(err)}). The fire drill may report this file as not the latest.`);
    }
}

function message(err: unknown): string {
    return err instanceof Error ? err.message : 'unknown error';
}

// ── M28: reconciling the record with the entry after a print ────────────────

/**
 * What a print of the ACTIVE kit entry owes the record.
 *
 *  - `refresh-bundle` — the record already names this set: point it at the new
 *    bundle and nothing else. A `PUT` here would reset `lasttestedat`, and the
 *    fire-drill history belongs to this set (§16.9).
 *  - `replace-record` — the record is missing or names ANOTHER set. Upsert it:
 *    the drill date it resets referred to a set that is no longer current.
 *  - `none` — nothing to do (a matching record on a surface with no bundle
 *    endpoint).
 */
export type RecordReconciliation = 'refresh-bundle' | 'replace-record' | 'none';

export function planRecordReconciliation(params: {
    /** The kit record as last read, or null when there is none. */
    record: { recoverySetId: string } | null;
    /** The set id of the entry that was just printed from. */
    entrySetId: string;
    /** False on surfaces with no bundle-refresh endpoint — the ORGANIZATION
     *  record has only GET/PUT/tested, and an organization has no bundle file
     *  for the record to be stale against (Addendum §14.4). */
    canRefreshBundle: boolean;
}): RecordReconciliation {
    if (!params.record) return 'replace-record';
    if (params.record.recoverySetId !== params.entrySetId) return 'replace-record';
    return params.canRefreshBundle ? 'refresh-bundle' : 'none';
}
